"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useClaimer = void 0;
var react_1 = require("react");
var web3_utils_1 = require("web3-utils");
var _abi_1 = require("../abi");
var _contracts_1 = require("../contracts");
var _launchpadConstants_1 = require("../launchpadConstants");
var _shared_1 = require("../shared");
var utils_1 = require("../shared/utils");
var multicall_1 = require("../shared/utils/multicall");
var DEFAULT_ACCOUNT = '0x0000000000000000000000000000000000000000';
// Claim becomes available in UI as soon as its time is near (now - threshold)
var CLAIM_TIME_THRESHOLD_SEC = 40;
var DEFAULT_VERSION = 'v4';
var gasPrices = {
    POLY: 420000000000,
    BSC: 5000000000,
};
function useClaimer(_a) {
    var token = _a.token, distribution = _a.distribution;
    var _b = (0, _shared_1.useWeb3Provider)(), targetChain = _b.targetChain, targetChainWeb3 = _b.targetChainWeb3;
    var account = (0, _shared_1.useUser)().account;
    var claimerAddress = distribution.claimerAddress, claimerVersion = distribution.claimerVersion, network = distribution.network;
    var symbol = distribution.symbol || token.symbol;
    var version = claimerVersion || DEFAULT_VERSION;
    var instance = (0, _contracts_1.useClaimerContract)(claimerAddress, 
    // @ts-expect-error
    version);
    var _c = (0, react_1.useState)([]), claims = _c[0], setClaims = _c[1];
    var _d = (0, react_1.useState)(_launchpadConstants_1.ZERO_BN), totalClaimed = _d[0], setTotalClaimed = _d[1];
    var _e = (0, react_1.useState)(_launchpadConstants_1.ZERO_BN), total = _e[0], setTotal = _e[1];
    var _f = (0, react_1.useState)(false), loaded = _f[0], setLoaded = _f[1];
    var _g = (0, react_1.useState)(false), paused = _g[0], setPaused = _g[1];
    var _h = (0, react_1.useState)(), isClaiming = _h[0], setIsClaiming = _h[1];
    var _j = (0, react_1.useState)(true), isEligible = _j[0], setIsEligible = _j[1];
    var sendOptions = {
        value: 0,
        from: account,
    };
    if (gasPrices[targetChain.symbol]) {
        sendOptions['gasPrice'] = gasPrices[targetChain.symbol];
    }
    (0, react_1.useEffect)(function () {
        if (!targetChainWeb3)
            return;
        var toNorm = function (v) { return (0, utils_1.normalizeDecimals)((0, web3_utils_1.toBN)(v), token.decimals); };
        if (version === 'v1' || version === 'v2') {
            var methods = {
                totalTokens: toNorm,
                isPaused: function (v) { return !!v; },
            };
            if (version === 'v1') {
                methods['claimedTokens'] = toNorm;
            }
            else {
                methods['getTotalClaimed'] = toNorm;
            }
            (0, multicall_1.multicallTransformed)({
                web3: targetChainWeb3,
                methods: methods,
                address: claimerAddress,
                abi: _abi_1.CONTRACTS.claimer[version].abi,
                returnCachedOnError: true,
            }).then(function (_a) {
                var totalTokens = _a[0], isPaused = _a[1], totalClaimed = _a[2];
                setTotal(totalTokens);
                setPaused(isPaused);
                setTotalClaimed(totalClaimed);
            });
        }
        else if (version === 'v3' || version === 'v4') {
            (0, multicall_1.multicallTransformed)({
                web3: targetChainWeb3,
                methods: {
                    isPaused: function (v) { return !!v; },
                    getTotalStats: function (v) { return v.map(function (v) { return toNorm(v); }); },
                },
                address: claimerAddress,
                abi: _abi_1.CONTRACTS.claimer[version].abi,
                returnCachedOnError: true,
            }).then(function (_a) {
                var isPaused = _a[0], totalStats = _a[1];
                setTotal(totalStats[0]);
                setPaused(isPaused);
                setTotalClaimed(totalStats[1]);
            });
        }
    }, [version, claimerAddress, targetChainWeb3, token.decimals]);
    // Refresh when new item becomes claimable
    (0, react_1.useEffect)(function () {
        if (!claims.length)
            return;
        var now = Date.now() / 1000;
        var times = claims.map(function (claim) { return claim.time; }).filter(function (t) { return t > now; });
        var nextTime = Math.min.apply(Math, times);
        if (times.length > 0) {
            var id_1 = setTimeout(refresh, (nextTime - now - CLAIM_TIME_THRESHOLD_SEC / 2) * 1000);
            return function () { return clearTimeout(id_1); };
        }
    }, [claims.length]);
    (0, react_1.useEffect)(function () {
        if (!instance)
            return;
        refresh().finally(function () { return setLoaded(true); });
    }, [instance, account]);
    function refresh() {
        // Do not fallback to the default account once loaded
        if (loaded && !account) {
            return;
        }
        var promises = [];
        if (version === 'v4' && account) {
            promises.push((0, utils_1.call)(instance, 'isAccountEligible', [account]).then(setIsEligible));
        }
        promises.push((0, utils_1.call)(instance, 'getClaims', [account || DEFAULT_ACCOUNT])
            .then(function (data) {
            setClaims(transformClaims(data, token.decimals));
        })
            .catch(function (e) { return console.error('Failed to fetch claims', e); }));
        return Promise.all(promises);
    }
    function claimTokens(idx) {
        if (!account)
            return;
        setIsClaiming(idx);
        return instance.methods
            .claim(account, idx)
            .send(sendOptions)
            .then(function () {
            refresh();
            return { message: "Claimed ".concat((0, utils_1.fNum)(claims[idx].amount), " ").concat(symbol) };
        })
            .catch(function (e) {
            return { error: "Failed to claim: ".concat((e === null || e === void 0 ? void 0 : e.message) || e.toString()) };
        })
            .finally(function () { return setIsClaiming(undefined); });
    }
    function claimAll() {
        if (!account)
            return;
        setIsClaiming(-1);
        return instance.methods
            .claimAll(account)
            .send(sendOptions)
            .then(function () {
            refresh();
            return { message: 'Claimed all' };
        })
            .catch(function () {
            return {
                error: 'Failed to claim or "Claim all" is not supported in this claimer',
            };
        })
            .finally(function () { return setIsClaiming(undefined); });
    }
    var userTotal = claims.reduce(function (acc, claim) { return acc.add(claim.amount); }, _launchpadConstants_1.ZERO_BN);
    var userTotalClaimed = claims.reduce(function (acc, claim) { return acc.add(claim.claimedAmount); }, _launchpadConstants_1.ZERO_BN);
    var availableClaims = claims.filter(function (c) { return c.isClaimable && c.claimedAmount.eqn(0); });
    return {
        // total
        total: total,
        totalClaimed: totalClaimed,
        loaded: loaded,
        paused: paused,
        isClaiming: isClaiming,
        symbol: symbol,
        chain: network,
        address: claimerAddress,
        // user
        userTotal: userTotal,
        userTotalClaimed: userTotalClaimed,
        availableClaims: availableClaims,
        claims: claims,
        isEligible: isEligible,
        // functions
        claimTokens: claimTokens,
        claimAll: claimAll,
    };
}
exports.useClaimer = useClaimer;
function transformClaims(data, decimals) {
    return data[0].map(function (rawTime, idx) {
        var amount = (0, utils_1.normalizeDecimals)((0, web3_utils_1.toBN)(data[2][idx]), decimals);
        var time = parseInt(rawTime);
        var claimedAmount = (0, utils_1.normalizeDecimals)((0, web3_utils_1.toBN)(data[4][idx]), decimals);
        var isClaimable = data[3][idx] &&
            // must have tokens allocated
            amount.gtn(0) &&
            // must not have it claimed
            claimedAmount.eqn(0) &&
            // claim must have time (0 - means TBA)
            time > 0 &&
            // current time must be within the threshold of the claim time
            time - CLAIM_TIME_THRESHOLD_SEC < Date.now() / 1000;
        return {
            idx: idx,
            time: time,
            percent: parseInt(data[1][idx]) / 1000,
            amount: amount,
            isClaimable: isClaimable,
            claimedAmount: claimedAmount,
        };
    });
}
